import { inject, Injectable } from '@angular/core';
import { from, map, switchMap, throwError, type Observable } from 'rxjs';
import { FilesApiService } from '../../../core/api/files-api.service';

/** Mirrors the API enum — set when the upload should be renormalized server-side. */
export type TranscodeProfile = 'voice-note-ogg';

export interface UploadedFile {
  fileId: string;
  contentType: string;
  filename: string;
  sizeBytes: number;
}

export interface UploadOptions {
  channelKind?: string;
  /** Signals the API to transcode the upload on confirm (e.g. WhatsApp voice-note PTT OGG). */
  transcodeProfile?: TranscodeProfile;
}

/**
 * Orchestrates the presigned upload flow: ask the API for an upload URL, PUT the bytes directly
 * to S3 (via native fetch, bypassing the auth interceptor that must not touch the S3 signature),
 * then confirm. Returns the metadata needed to attach the file to a message.
 */
@Injectable({ providedIn: 'root' })
export class FileUploadService {
  private readonly filesApi = inject(FilesApiService);

  upload(file: File, options: UploadOptions = {}): Observable<UploadedFile> {
    return this.filesApi
      .issueUploadUrl({
        module: 'conversations',
        contentType: file.type,
        filename: file.name,
        sizeBytes: file.size,
        channelKind: options.channelKind,
        transcodeProfile: options.transcodeProfile,
      })
      .pipe(
        switchMap((issued) =>
          from(fetch(issued.uploadUrl, { method: 'PUT', headers: issued.requiredHeaders, body: file })).pipe(
            switchMap((response) =>
              response.ok
                ? this.filesApi.confirmUpload(issued.fileId)
                : throwError(() => new Error(`upload failed with status ${response.status}`)),
            ),
            map(() => ({
              fileId: issued.fileId,
              contentType: file.type,
              filename: file.name,
              sizeBytes: file.size,
            })),
          ),
        ),
      );
  }
}
